import { CategoryKey, CategoryTypeKey } from "@/core/entities/deduction";
import {
  GET_MESSAGES,
  GET_CONVERSATION_BY_REMITTANCE_ITEM_ID,
  POST_MESSAGE,
  GET_CONVERSATIONS,
  GET_CONVERSATION_BY_REFERENCE_ID,
} from "@/core/graphql/conversation";
import {
  GetConversationByRemittanceItemIdResponse,
  GetConversationMessagesParams,
  GetConversationsParams,
  IGetConversationsResponse,
  IConversationRepository,
  IMessageQueryPaginatedResponse,
  IPostConversationMessageResponse,
  IUploadAttachmentResponse,
  PostConversationMessageParams,
  UploadAttachmentParams,
  GetConversationByReferenceIdResponse,
} from "@/core/repositories/conversation";
import { PrivateHttp } from "@/infrastructure/adapters/http";
import { ApiEndpoints } from "@/infrastructure/services/config";
import { ApolloClient } from "@apollo/client";
import { ContentType } from "@ir/core/http";

export class ConversationRepository implements IConversationRepository {
  constructor(private readonly client: ApolloClient) { }

  async getConversations(params: GetConversationsParams): Promise<IGetConversationsResponse> {
    const { data } = await this.client.query<IGetConversationsResponse>({
      query: GET_CONVERSATIONS,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) {
      throw new Error("Failed to get conversations");
    }

    return data;
  }

  async getConversationByRemittanceItemId(
    remittanceItemId: number
  ): Promise<GetConversationByRemittanceItemIdResponse> {
    const { data } = await this.client.query<GetConversationByRemittanceItemIdResponse>({
      query: GET_CONVERSATION_BY_REMITTANCE_ITEM_ID,
      variables: { params: { remittanceItemId } },
    });

    if (!data) {
      throw new Error("Failed to get conversation by remittance item id");
    }

    return data;
  }

  async getConversationByReferenceId(
    referenceId: number,
    categoryKey: CategoryKey,
    categoryTypeKey?: CategoryTypeKey
  ): Promise<GetConversationByReferenceIdResponse> {
    const { data } = await this.client.query<GetConversationByReferenceIdResponse>({
      query: GET_CONVERSATION_BY_REFERENCE_ID,
      variables: { params: { referenceId, categoryKey, categoryTypeKey } },
    });

    if (!data) {
      throw new Error("Failed to get conversation by reference_id");
    }

    return data;
  }

  async postConversationMessage(params: PostConversationMessageParams): Promise<IPostConversationMessageResponse> {
    const { data } = await this.client.mutate<IPostConversationMessageResponse>({
      mutation: POST_MESSAGE,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to post message");
    }

    return data;
  }
  async getConversationMessages(
    params: GetConversationMessagesParams,
    refetch: boolean = false
  ): Promise<IMessageQueryPaginatedResponse> {
    const { data } = await this.client.query<IMessageQueryPaginatedResponse>({
      query: GET_MESSAGES,
      variables: { params },
      fetchPolicy: refetch ? "no-cache" : "cache-first",
    });

    if (!data) {
      throw new Error("Failed to get messages");
    }

    return data;
  }

  async uploadAttachment(params: UploadAttachmentParams): Promise<IUploadAttachmentResponse> {
    const formData = new FormData();
    formData.append("file", params.file);

    return await new PrivateHttp({
      headers: {
        "Content-Type": ContentType.MULTIPART,
      },
    }).post<IUploadAttachmentResponse>(ApiEndpoints.UPLOAD_CONVERSATION_ATTACHMENT, formData);
  }
}
